
import {
  formatByCountry,
  dateToDateString,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { HandoverController } from "@/app/ui/controllers/HandoverController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  HandoverDetail,
  HandoverSttResult,
  SttData
} from "@/domain/entities/Handover";
import { Vue } from "vue-class-component";
import generateHandoverManifest from "./manifest-handover";
import generateSummaryPdf from "@/app/ui/modules/summary";
import { getToken } from "@/app/infrastructures/misc/Cookies";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import jsPDF from "jspdf";
import { AxiosError } from "axios";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import { logoBlack } from "@/app/ui/modules/receipt-v2/constants/images";
import JsBarcode from "jsbarcode";
import { printReceiptDhl } from "./print-receipt-dhl";
import { printReceiptVendor } from "./print-receipt-vendor";
import { PDFDocument } from "pdf-lib";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { createI18n } from "vue-i18n";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";

export default class Print extends Vue {
  async print(accountType: string, id = 0) {
    MainAppController.showLoading();
    // logo
    const logo = await import(`@/app/ui/assets/images/logo-${accountType}.png`);
    const printManifest =
      !id || (await HandoverController.getDetailHandover(id));

    if (printManifest) {
      const detailHandover = HandoverController.detailHandover;

      const countryCode = AccountController.accountData.account_type_detail?.countryCode;
      const data = detailHandover.stt.map(
        (item: HandoverSttResult, index: number) => ({
          no: index + 1,
          sttNo: item.sttNo,
          product: item.sttProductType,
          commodity:
            AccountController.accountData.account_type_detail.countryCode.toLowerCase() ===
            "id"
              ? item.sttCommodityName
              : item.sttCommodityNameEn,
          koli: item.sttTotalPiece,
          grossWeight: formatByCountry(item.sttGrossWeight, { type: 'weight', country: countryCode, fractionDigits: 2 }),
          volumeWeight: formatByCountry(item.sttVolumeWeight, { type: 'weight', country: countryCode, fractionDigits: 2 }),
          origin: item.sttOriginCityId,
          destination: item.sttDestinationCityId,
          notes: ""
        })
      );
      await generateHandoverManifest(detailHandover, data, logo.default);
    }
    MainAppController.closeLoading();
  }

  async printSummary(accountType: string, id = 0) {
    MainAppController.showLoading();
    // logo
    const logo = await import(`@/app/ui/assets/images/logo-${accountType}.png`);
    const printManifest =
      !id || (await HandoverController.getDetailHandover(id));
    if (printManifest) {
      const detail: HandoverDetail = HandoverController.detailHandover;

      const data: Array<any> = [
        {
          name: "handover.manifest.destinationCity",
          value: `${detail.handoverArrivalCitycode} - ${detail.handoverArrivalCityName}`
        },
        {
          name: "handover.manifest.consolidatorName",
          value: AccountController.accountData.name
        },
        {
          name: "handover.manifest.handoverTo",
          value: detail.handoverDestinationPartnerName
        },
        {
          name: "handover.manifest.handoverDate",
          value: dateToDateString(detail.handoverCreatedAt)
        },
        {
          name: "handover.manifest.totalStt",
          value: detail.handoverManifestTotalStt
        },
        {
          name: "handover.manifest.totalPieces",
          value: detail.handoverTotalPiece
        },
        {
          name: "handover.manifest.totalGrossWeight",
          value: `${formatByCountry(
            detail.handoverManifestTotalGrossWeight,
            { type: 'weight', country: AccountController.accountData.account_type_detail?.countryCode, fractionDigits: 2 }
          )} Kg`
        },
        {
          name: "handover.manifest.totalDimensionWeight",
          value: `${formatByCountry(
            detail.handoverManifestTotalVolumeWeight,
            { type: 'weight', country: AccountController.accountData.account_type_detail?.countryCode, fractionDigits: 2 }
          )} Kg`
        }
      ];

      await generateSummaryPdf(
        `Handover Manifest - ${detail.handoverManifestId}`,
        data,
        logo.default
      );
    }
    MainAppController.closeLoading();
  }

  async getBlobFile(
    url: string,
    isAuth?: boolean,
    timeout = 60000,
    method?: string,
    payload?: any,
    withJsonContentType?: boolean
  ) {
    MainAppController.showLoading();
    const headers = new Headers();
    const auth = {
      token: `Bearer ${getToken()}`,
      baseUrl: process.env.VUE_APP_GENESIS_API_URL
    };
    headers.append("authorization", auth.token);

    if (withJsonContentType) {
      headers.append("Content-Type", "application/json");
    }

    // https://stackoverflow.com/questions/46946380/fetch-api-request-timeout
    return new Promise<Blob>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject({ response: { status: 504 } });
      }, timeout);

      // call api
      const parsedUrl = parsingLinkGcpToAws(url);
      fetch(parsedUrl, {
        headers: isAuth ? headers : undefined,
        method: method ? method : undefined,
        body: payload ? JSON.stringify(payload) : undefined
      })
        .then(async (response: any) => {
          if (response.ok) {
            try {
              const blob = await response.blob();
              clearTimeout(timer);
              resolve(blob);
            } catch (e) {
              clearTimeout(timer);
              reject(e);
            }
          } else {
            let data: any = null;
            try {
              data = await response.json();
            } catch (_) {
              // ignore JSON parse errors
            }
            clearTimeout(timer);
            (response as any).data = data;
            reject({ response });
          }
        })
        .catch((error: any) => {
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  private vendorReceiptTranslator() {
    const i18nInstance = createI18n({
      legacy: false,
      locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
      fallbackLocale: "id",
      globalInjection: false,
      messages: {
        en,
        id,
        my
      }
    });
    return i18nInstance.global.t;
  }

  private pendingItemMessage(item: SttData): string {
    const message = AccountController.accountData.accountIsForeign
      ? item.messageEn
      : item.message;
    return message || "-";
  }

  private showPendingLabelsModal(pendingData: SttData[], translate: any) {
    const messageData = pendingData.map((item: SttData) => {
      return `<div style="display:flex;gap:4px;white-space:nowrap;overflow:hidden;"><span style="font-weight:600;flex-shrink:0;">${item.sttVendorSttNo}:</span><span style="overflow:hidden;text-overflow:ellipsis;">${this.pendingItemMessage(
        item
      )}</span></div>`;
    });
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "image-modal-warning",
        title: translate("Beberapa label tidak dapat dicetak") as string,
        message: `<div style="max-height:200px;overflow-y:auto;text-align:left;">${messageData.join(
          ""
        )}</div>`,
        onClose: () => {
          MainAppController.closeMessageModal();
        },
        textCancel: translate("Kembali") as string,
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6"
      })
    );
  }

  private showAllPendingError(translate: any) {
    MainAppController.showErrorMessage(
      parsingErrorResponse(
        new AxiosError("Gagal Print Resi Vendor", "400", undefined, undefined, {
          status: 400,
          data: {
            message: {
              id: "Semua label tidak dicetak",
              en: "None of the labels are printed."
            }
          }
        } as any),
        translate("Gagal Print Resi Vendor") as string
      )
    );
  }

  private assertValidLabelData(unPendingData: SttData[]) {
    const hasValidLabel = unPendingData.filter(
      (item: SttData) => item.resi.includes("https") || item.detail.sttId
    ).length;
    if (!hasValidLabel && unPendingData.length) {
      throw new AxiosError("Invalid Label Data", "400", undefined, undefined, {
        status: 400,
        data: {
          message: {
            id: "Data tidak sesuai",
            en: "Invalid  data"
          }
        }
      } as any);
    }
  }

  private async printUnpendingLabels(unPendingData: SttData[], code: string) {
    const printV2Data = unPendingData.filter((item: SttData) => item.detail.sttId);
    if (printV2Data.length) {
      this.printVendorReceiptV2(printV2Data, code);
      return;
    }
    const blobs: Blob[] = [];
    for (const item of unPendingData) {
      const blob = await this.getBlobFile(item.resi, true);
      let newBlob = blob;
      if (newBlob.type === "application/octet-stream") {
        newBlob = new Blob([newBlob], { type: "application/pdf" });
      }
      blobs.push(newBlob);
    }
    this._blobsToPdf(blobs);
  }

  async printVendorReceipt(id: number, code = "") {
    const translate = this.vendorReceiptTranslator();
    try {
      MainAppController.showLoading();
      const res = await HandoverController.getHandoverReceipt(id);
      const unPendingData = res.filter((item: SttData) => !item.awbPending);
      const pendingData = res.filter((item: SttData) => item.awbPending);

      this.assertValidLabelData(unPendingData);

      if (unPendingData.length) {
        await this.printUnpendingLabels(unPendingData, code);
        if (pendingData.length) {
          this.showPendingLabelsModal(pendingData, translate);
        }
      } else {
        this.showAllPendingError(translate);
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          translate("Gagal Print Resi Vendor") as string,
          () => this.printVendorReceipt(id)
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  bookingTypePayload(printData: PrintDataEntitas) {
    let type = "client";
    if (printData.sttReverseJourneyShipmentId.match(/^ARA|^ARB|^AP|^AS/)) {
      type = "manual";
    }
    if (
      (printData.sttShipmentId && printData.sttClientId === 0) ||
      (printData.sttShipmentId && printData.sttClientId !== 0)
    ) {
      type = "shipment";
    }

    if (!printData.sttShipmentId && printData.sttClientId === 0) {
      type = "manual";
    }
    return type;
  }


  async printVendorReceiptV2(data: SttData[], code = "") {
    const doc: any = new jsPDF("p", "mm", "a6", true);

    for (const [indexStt, item] of data.entries()) {
      for (const [indexPiece, piece] of item.detail.sttPieces.entries()) {
        /* barcode */
        const barcode = document.createElement("img");
        JsBarcode(barcode, item.sttVendorSttNo, {
          textAlign: "left",
          displayValue: false,
          fontSize: 16
        });

        /* vendor ref code */
        let vendorRefCode: any = "";
        if (item.sttVendorReferenceNo) {
          vendorRefCode = document.createElement("img");
          JsBarcode(vendorRefCode, item.sttVendorReferenceNo, {
            textAlign: "left",
            displayValue: false,
            fontSize: 16
          });
        }

        if (indexStt || indexPiece) {
          doc.addPage("a6", "p");
        }
        let language = item.detail.sttProductType === "INTERPACK" ? "en" : "id";

        if (AccountController.accountData.accountIsForeign) language = "en";

        if (code === "DL") {
          await printReceiptDhl(
            {
              doc,
              bookingType: this.bookingTypePayload(new PrintDataEntitas(item.detail)),
              accountType: AccountController.accountData.account_type,
              data: new PrintDataEntitas(item.detail),
              pieceData: piece,
              barcode: barcode.src,
              vendorRefCode: vendorRefCode?.src,
              barcodeValue: item.sttVendorSttNo,
              vendorRefCodeValue: item.sttVendorReferenceNo,
              logo: logoBlack,
              shipmentId: "",
              startX: 0,
              startY: 0
            },
            language
          );
        } else {
          await printReceiptVendor(
            {
              doc,
              bookingType: this.bookingTypePayload(new PrintDataEntitas(item.detail)),
              accountType: AccountController.accountData.account_type,
              data: new PrintDataEntitas(item.detail),
              pieceData: piece,
              barcode: barcode.src,
              vendorRefCode: vendorRefCode?.src,
              barcodeValue: item.sttVendorSttNo,
              vendorRefCodeValue: item.sttVendorReferenceNo,
              logo: logoBlack,
              shipmentId: "",
              startX: 0,
              startY: 0
            },
            language
          );
        }
      }
    }

    doc.setProperties({
      title: `resi-thermal - ${data?.[0].sttVendorSttNo} - ${data?.[0]?.sttVendorReferenceNo}.pdf`
    });

    doc.autoPrint();

    window.open(doc.output("bloburl"), "_blank");
  }

  _loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = window.URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        resolve(img);
      };

      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };

      img.src = url;
    });
  }

  async _blobsToPdf(blobs: Blob[]) {
    if (!blobs.length) return;

    const firstBlob = blobs[0];

    // =========================
    // PDF -> MERGE
    // =========================
    if (firstBlob.type === "application/pdf") {
      const mergedPdf = await PDFDocument.create();

      for (const blob of blobs) {
        const bytes = await blob.arrayBuffer();

        const pdf = await PDFDocument.load(bytes);

        const pages = await mergedPdf.copyPages(
          pdf,
          pdf.getPageIndices()
        );

        pages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      }

      const mergedBytes = await mergedPdf.save();

      const mergedBlob = new Blob([mergedBytes], {
        type: "application/pdf"
      });

      const url = URL.createObjectURL(mergedBlob);

      window.open(url, "_blank");

      return;
    }

    // =========================
    // IMAGE -> PDF
    // =========================
    const doc = new jsPDF({
      unit: "pt",
      format: "a6"
    });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const margin = 24;

    const maxW = pageW - margin * 2;
    const maxH = pageH - margin * 2;

    for (let i = 0; i < blobs.length; i++) {
      const img = await this._loadImageFromBlob(
        blobs[i]
      );

      const scale = Math.min(
        maxW / img.width,
        maxH / img.height,
        1
      );

      const w = img.width * scale;
      const h = img.height * scale;

      const x = (pageW - w) / 2;
      const y = (pageH - h) / 2;

      if (i !== 0) {
        doc.addPage();
      }

      doc.addImage(img, "PNG", x, y, w, h);
    }

    doc.autoPrint();

    window.open(doc.output("bloburl") as any, "_blank");
  }
}
